import { debugLog } from "../utils/debug";
import { REGISTRATION_BASE_URL } from "../config/environment";
import { getImpersonationHeaders } from "../context/ImpersonationContext";

/**
 * Registration API Service
 * Handles all API calls to the new registration system
 */
class RegistrationService {
  constructor() {
    this.baseUrl = REGISTRATION_BASE_URL;
  }

  /**
   * Get the base URL for direct API calls
   * @returns {string} Base URL
   */
  getBaseUrl() {
    return this.baseUrl;
  }

  /**
   * Make authenticated API request
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} API response
   */
  async makeRequest(endpoint, options = {}, token) {
    try {
      const url = `${this.baseUrl}${endpoint}`;

      // Get impersonation headers if impersonation is active
      const impersonationHeaders = getImpersonationHeaders();

      const config = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
          ...impersonationHeaders,
          ...options.headers,
        },
        ...options,
      };

      debugLog(`Making ${config.method} request to:`, url);
      if (impersonationHeaders['X-Impersonate-User']) {
        debugLog('🎭 Impersonating user:', impersonationHeaders['X-Impersonate-User']);
      }

      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();
      debugLog("API response:", data);

      return data;
    } catch (error) {
      debugLog("API request failed:", error);
      throw error;
    }
  }

  // ============================================================================
  // USER MANAGEMENT
  // ============================================================================

  /**
   * Get current user's profile
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} User profile
   */
  async getProfile(token) {
    const data = await this.makeRequest("/profile", {}, token);
    return data.user || null;
  }

  /**
   * Get all coaches (for selection use usfsOnly: true for USFS + approved interclub coaches).
   * @param {string} token - Firebase ID token
   * @param {Object} options - { usfsOnly: boolean, interclubId?: string } When usfsOnly, returns USFS-synced/linked coaches plus approved coaches for the user's (or given) interclub.
   * @returns {Promise<Array>} Coaches list
   */
  async getCoaches(token, options = {}) {
    const params = new URLSearchParams();
    if (options.usfsOnly) params.set("usfsOnly", "true");
    if (options.interclubId) params.set("interclubId", options.interclubId);
    const query = params.toString() ? `?${params.toString()}` : "";
    const data = await this.makeRequest(`/coaches${query}`, {}, token);
    return data.coaches || [];
  }

  /**
   * Update current user's profile
   * @param {Object} profileData - Profile data to update
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Updated profile
   */
  async updateProfile(profileData, token) {
    const data = await this.makeRequest(
      "/profile",
      {
        method: "PUT",
        body: JSON.stringify(profileData),
      },
      token
    );
    return data.user || null;
  }

  /**
   * Get all users (Admin only)
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Users list
   */
  async getAllUsers(token) {
    return this.makeRequest("/users", {}, token);
  }

  // ============================================================================
  // SKATER MANAGEMENT
  // ============================================================================

  /**
   * Create a new skater
   * @param {Object} skaterData - Skater data
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Created skater
   */
  async createSkater(skaterData, token) {
    return this.makeRequest(
      "/skaters",
      {
        method: "POST",
        body: JSON.stringify(skaterData),
      },
      token
    );
  }

  /**
   * Get all skaters for current user
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Skaters list
   */
  async getSkaters(token) {
    const data = await this.makeRequest("/skaters", {}, token);
    return data.skaters || [];
  }

  /**
   * Get all skaters coached by current user
   * @param {string} token - Firebase ID token
   * @returns {Promise<Array>} Coached skaters list
   */
  async getCoachedSkaters(token) {
    const data = await this.makeRequest("/skaters/coached", {}, token);
    return data.skaters || [];
  }

  /**
   * Get all skaters followed by the coach
   * @param {string} token - Firebase ID token
   * @returns {Promise<Array>} Followed skaters list
   */
  async getFollowedSkaters(token) {
    const data = await this.makeRequest("/coaches/followed-skaters", {}, token);
    return data.skaters || [];
  }

  /**
   * Follow a skater by their USFS membership ID
   * @param {string} membershipId - USFS membership ID
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Result with skater info
   */
  async followSkater(membershipId, token) {
    return await this.makeRequest(
      "/coaches/follow-skater",
      {
        method: "POST",
        body: JSON.stringify({ membershipId }),
      },
      token
    );
  }

  /**
   * Unfollow a skater
   * @param {string} skaterId - Skater ID
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Result
   */
  async unfollowSkater(skaterId, token) {
    return await this.makeRequest(
      `/coaches/follow-skater/${skaterId}`,
      { method: "DELETE" },
      token
    );
  }

  /**
   * Link an existing skater to the coach by USFS membership ID
   * This adds the coach to the skater's coaches array
   * @param {string} membershipId - USFS membership ID
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Result with skater info
   */
  async linkSkaterToCoach(membershipId, token) {
    return await this.makeRequest(
      "/coaches/link-skater",
      {
        method: "POST",
        body: JSON.stringify({ membershipId }),
      },
      token
    );
  }

  /**
   * Get all skaters in the club (for club admins)
   * @param {string} token - Firebase ID token
   * @returns {Promise<Array>} Club skaters list
   */
  async getClubSkaters(token) {
    const data = await this.makeRequest("/club-admin/skaters", {}, token);
    return data.skaters || [];
  }

  /**
   * Get all skaters in the interclub (for interclub admins)
   * @param {string} token - Firebase ID token
   * @returns {Promise<Array>} Interclub skaters list
   */
  async getInterclubSkaters(token) {
    const data = await this.makeRequest("/interclub-admin/skaters", {}, token);
    return data.skaters || [];
  }

  /**
   * Create season entry fee checkout session
   * @param {Object} payload - seasonId, skaterIds, successUrl, cancelUrl
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Checkout session
   */
  async createSeasonFeeCheckout(payload, token) {
    const data = await this.makeRequest(
      "/season-fee/checkout",
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
      token
    );
    return data || null;
  }

  /**
   * Confirm a completed checkout session
   * @param {string} sessionId - Stripe Checkout Session ID
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Confirmation result
   */
  async confirmSeasonFeePayment(sessionId, token, stripeAccountId) {
    const data = await this.makeRequest(
      "/season-fee/confirm",
      {
        method: "POST",
        body: JSON.stringify({ sessionId, stripeAccountId }),
      },
      token
    );
    return data || null;
  }

  /**
   * Get season registration status for current user
   * @param {string} seasonId - Season ID
   * @param {string} token - Firebase ID token
   * @returns {Promise<Array>} Registrations list
   */
  async getSeasonRegistrations(seasonId, token, skaterIds = []) {
    const params = new URLSearchParams({ seasonId });
    if (skaterIds.length > 0) {
      params.set("skaterIds", skaterIds.join(","));
    }
    const data = await this.makeRequest(`/season-registrations?${params.toString()}`, {}, token);
    return data.registrations || [];
  }

  /**
   * Get a specific skater by ID
   * @param {string} skaterId - Skater ID
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Skater data
   */
  async getSkater(skaterId, token) {
    const data = await this.makeRequest(`/skaters/${skaterId}`, {}, token);
    return data.skater || null;
  }

  /**
   * Update a skater
   * @param {string} skaterId - Skater ID
   * @param {Object} skaterData - Skater data to update
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Updated skater
   */
  async updateSkater(skaterId, skaterData, token) {
    const data = await this.makeRequest(
      `/skaters/${skaterId}`,
      {
        method: "PUT",
        body: JSON.stringify(skaterData),
      },
      token
    );
    return data.skater || null;
  }

  /**
   * Delete a skater (soft delete)
   * @param {string} skaterId - Skater ID
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Success message
   */
  async deleteSkater(skaterId, token) {
    return this.makeRequest(
      `/skaters/${skaterId}`,
      {
        method: "DELETE",
      },
      token
    );
  }

  /**
   * Search skaters (Admin only)
   * @param {string} searchTerm - Search query
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Search results
   */
  async searchSkaters(searchTerm, token) {
    const data = await this.makeRequest(
      `/skaters/search?q=${encodeURIComponent(searchTerm)}`,
      {},
      token
    );
    return data.skaters || [];
  }

  /**
   * Get all skaters (Admin only)
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} All skaters
   */
  async getAllSkaters(token) {
    const data = await this.makeRequest("/skaters/all", {}, token);
    return data.skaters || [];
  }

  // ============================================================================
  // STATISTICS
  // ============================================================================

  /**
   * Get skater statistics for current user
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Statistics
   */
  async getStats(token) {
    const data = await this.makeRequest("/stats", {}, token);
    return data.stats || null;
  }

  /**
   * Get system-wide skater statistics (Admin only)
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} System statistics
   */
  async getAllStats(token) {
    const data = await this.makeRequest("/stats/all", {}, token);
    return data.stats || null;
  }

  // ============================================================================
  // ADMIN MANAGEMENT
  // ============================================================================

  /**
   * Create a new user (Admin only)
   * @param {Object} userData - User data
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Created user
   */
  async createUser(userData, token) {
    return this.makeRequest(
      "/admin/users",
      {
        method: "POST",
        body: JSON.stringify(userData),
      },
      token
    );
  }

  /**
   * Update a user (Admin only)
   * @param {string} uid - User ID
   * @param {Object} userData - User data to update
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Updated user
   */
  async updateUser(uid, userData, token) {
    return this.makeRequest(
      `/admin/users/${uid}`,
      {
        method: "PUT",
        body: JSON.stringify(userData),
      },
      token
    );
  }

  /**
   * Promote user to admin (Super Admin only)
   * @param {string} uid - User ID
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Success message
   */
  async promoteToAdmin(uid, token) {
    return this.makeRequest(
      `/admin/users/${uid}/promote`,
      {
        method: "POST",
      },
      token
    );
  }

  /**
   * Demote admin user (Super Admin only)
   * @param {string} uid - User ID
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Success message
   */
  async demoteFromAdmin(uid, token) {
    return this.makeRequest(
      `/admin/users/${uid}/demote`,
      {
        method: "POST",
      },
      token
    );
  }
}

const registrationService = new RegistrationService();

export default registrationService;
